import type {
  UseMutationOptions,
  UseQueryOptions,
  UseSuspenseQueryOptions,
} from "@tanstack/react-query";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CheckoutRequest,
  CheckoutResponse,
  CustomerResponse,
  ScheduledChangesResponse,
  SubscriptionResponse,
} from "@/app/billing/types";
import { createCheckout } from "./checkout";
import {
  customerQueryOptions,
  type UpdateCustomerRequest,
  updateCustomer,
} from "./get-customer";
import { recentInvoicesQueryOptions } from "./get-recent-invoices";
import { scheduledChangesQueryOptions } from "./get-scheduled-changes";
import { subscriptionQueryOptions } from "./get-subscriptions";
import { upcomingInvoiceQueryOptions } from "./get-upcoming-invoice";
import { type UsageQueryParams, usageQueryOptions } from "./get-usage";
import { billingKeys } from "./query-keys";

/** Suspense variant: use when data must be present during render. */
export const useSuspenseGetSubscription = (
  organizationId: string,
  options?: Omit<
    UseSuspenseQueryOptions<SubscriptionResponse>,
    "queryKey" | "queryFn"
  >
) =>
  useSuspenseQuery({
    ...subscriptionQueryOptions({ organizationId }),
    ...options,
  });

/** Non-suspense variant: use for conditional/gated UI with loading states. */
export const useGetSubscription = (
  organizationId: string,
  options?: Omit<UseQueryOptions<SubscriptionResponse>, "queryKey" | "queryFn">
) =>
  useQuery({
    ...subscriptionQueryOptions({ organizationId }),
    ...options,
  });

export const useGetUsage = (
  organizationId: string,
  params?: UsageQueryParams
) => useSuspenseQuery({ ...usageQueryOptions({ organizationId, params }) });

export const useGetCustomer = (organizationId: string) =>
  useSuspenseQuery({ ...customerQueryOptions({ organizationId }) });

export const useGetRecentInvoices = (organizationId: string) =>
  useSuspenseQuery({ ...recentInvoicesQueryOptions({ organizationId }) });

export const useGetUpcomingInvoice = (organizationId: string) =>
  useSuspenseQuery({ ...upcomingInvoiceQueryOptions({ organizationId }) });

export const useGetScheduledChanges = (
  organizationId: string,
  options?: Omit<
    UseSuspenseQueryOptions<ScheduledChangesResponse>,
    "queryKey" | "queryFn"
  >
) =>
  useSuspenseQuery({
    ...scheduledChangesQueryOptions({ organizationId }),
    ...options,
  });

export const useUpdateCustomer = (
  organizationId: string,
  options?: Omit<
    UseMutationOptions<CustomerResponse, Error, UpdateCustomerRequest>,
    "mutationFn"
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCustomerRequest) =>
      updateCustomer({ data, organizationId }),
    meta: {
      invalidates: [billingKeys.subscriptions(organizationId)],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(billingKeys.customer(organizationId), data);
      toast.success("Customer information updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update customer: ${error.message}`);
    },
    ...options,
  });
};

export const useCreateCheckout = (
  organizationId: string,
  options?: Omit<
    UseMutationOptions<CheckoutResponse, Error, CheckoutRequest>,
    "mutationFn"
  >
) =>
  useMutation({
    mutationFn: (data: CheckoutRequest) =>
      createCheckout({ data, organizationId }),
    ...options,
  });
